import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { useOnlineStatus } from '../../../hooks'
import { db, Pocket } from '../../../utils/dexie/db'
import { requestAccessToken } from '../../../api/auth.api'
import { syncCompanyPocketsData } from '../../../api/customers.api'
import { handleDecryptPocketData, handleDecryptPocketsData, handleEncryptPocketData } from '../utils'

export const pocketsWasFirstLoaded = () => {
  return sessionStorage.getItem('pocketsFirstLoaded')
}

const fetchLocalPockets = async (): Promise<Array<Pocket>> => {
  try {
    if (!db.isOpen()) {
      await db.open().catch(async (err) => {
        console.error('Failed to open IndexedDB:', err)
      })
    }

    return db.pockets.where('deleted').equals(0).toArray()
  } catch (error) {
    console.error('IndexedDB fetch error:', error)
    return [] // Return empty array on error
  }
}

export const deleteLocalPockets = async (): Promise<void> => {
  const deletedPocketsData = await db.pockets
    .where('deleted')
    .equals(1)
    .and((pocket: Pocket) => pocket.synced === 1)
    .toArray()
  const deletedIds = new Set(deletedPocketsData.map((pocket: Pocket) => String(pocket.id)))

  for (const deletedId of deletedIds) {
    await db.pockets.delete(deletedId)
  }

  console.log('Database cleaned')
}

export const deletePocketLocally = async (pocketId: string, pocketToTransferId: string, updatedBalanceTransfered: string, updatedBalanceZero: string, secret: string): Promise<Array<Pocket>> => {
  await db.pockets.update(pocketId, { deleted: 1, balance: updatedBalanceZero, fundsTransferedTo: pocketToTransferId, updatedAt: new Date().getTime(), synced: 0 })
  await db.pockets.update(pocketToTransferId, { balance: updatedBalanceTransfered, updatedAt: new Date().getTime(), synced: 0 })

  const pocketsDataEncrypted = await fetchLocalPockets()
  return handleDecryptPocketsData(pocketsDataEncrypted, secret)
}

export const updatePocketLocally = async (pocketId: string, pocketDataToUpdate: Pocket, secret: string): Promise<Array<Pocket>> => {
  const pocketDataEncrypted = await handleEncryptPocketData({ ...pocketDataToUpdate, updatedAt: new Date().getTime(), synced: 0 }, secret)
  await db.pockets.update(pocketId, { ...pocketDataEncrypted })

  const pocketsDataEncrypted = await fetchLocalPockets()
  return handleDecryptPocketsData(pocketsDataEncrypted, secret)
}

export const addPocketLocally = async (newPocket: Pocket, secret: string): Promise<Array<Pocket>> => {
  const pocketDataEncrypted = await handleEncryptPocketData({ ...newPocket, synced: 0 }, secret)
  await db.pockets.put({ ...pocketDataEncrypted })

  const pocketsDataEncrypted = await fetchLocalPockets()
  return handleDecryptPocketsData(pocketsDataEncrypted, secret)
}

export const setDataWithOfflineStatus = async (): Promise<void> => {
  const pocketsData = await db.pockets.toArray()

  for (const pocketData of pocketsData) {
    await db.pockets.update(String(pocketData.id), { synced: 0 })
  }

  console.log('All data in offline status')
}

const syncPocketsWithServer = async (secret: string, refreshToken: string) => {
  const unsyncedPockets = await db.pockets.where('synced').equals(0).toArray()
  if (unsyncedPockets.length === 0) return

  try {
    const unsyncedPocketsDecryptedData = []

    for (const unsyncedPocket of unsyncedPockets) {
      const unsyncedPocketDecryptedData = await handleDecryptPocketData(unsyncedPocket, secret)
      unsyncedPocketsDecryptedData.push(unsyncedPocketDecryptedData)
    }

    const {
      data: { accessToken }
    } = await requestAccessToken(refreshToken)
    console.log('unsyncedPocketsDecryptedData', unsyncedPocketsDecryptedData)
    await syncCompanyPocketsData({ pockets: unsyncedPocketsDecryptedData }, accessToken)

    return true
  } catch (error) {
    console.error('Sync failed:', error)
  }
}

export const usePockets = (secret: string) => {
  const isOnline = useOnlineStatus()
  const queryClient = useQueryClient()

  const { data: pockets } = useQuery({
    queryKey: ['pockets', secret],
    queryFn: () => fetchLocalPockets()
  })

  const addPocketMutation = useMutation<void, Error, { newPocket: Pocket; secret: string; refreshToken: string }>({
    mutationFn: async ({ newPocket, secret, refreshToken }) => {
      const pocketDataEncrypted = await handleEncryptPocketData({ ...newPocket, synced: 0 }, secret)
      await db.pockets.put({ ...pocketDataEncrypted })

      if (isOnline) {
        await syncPocketsWithServer(secret, refreshToken)
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pockets', secret] })
  })

  const deletePocketMutation = useMutation<void, Error, { pocketId: string; pocketToTransferId: string; updatedBalanceTransfered: number; secret: string; refreshToken: string }>({
    mutationFn: async ({ pocketId, pocketToTransferId, updatedBalanceTransfered, secret, refreshToken }) => {
      await db.pockets.update(pocketId, { deleted: 1, balance: 0, updatedAt: new Date().getTime(), synced: 0 })
      await db.pockets.update(pocketToTransferId, { balance: updatedBalanceTransfered, updatedAt: new Date().getTime(), synced: 0 })

      if (isOnline) {
        await syncPocketsWithServer(secret, refreshToken)
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pockets', secret] })
  })

  const updatePocketMutation = useMutation<void, Error, { pocketId: string; pocketDataToUpdate: Pocket; secret: string; refreshToken: string }>({
    mutationFn: async ({ pocketId, pocketDataToUpdate, secret, refreshToken }) => {
      const pocketDataEncrypted = await handleEncryptPocketData({ ...pocketDataToUpdate, updatedAt: new Date().getTime(), synced: 0 }, secret)
      await db.pockets.update(pocketId, { ...pocketDataEncrypted })

      if (isOnline) {
        await syncPocketsWithServer(secret, refreshToken)
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pockets', secret] })
  })

  const syncMutation = useMutation<void, Error, { secret: string; refreshToken: string }>({
    mutationFn: async ({ secret, refreshToken }) => {
      syncPocketsWithServer(secret, refreshToken)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pockets', secret] })
  })

  const refetchPockets = async (secret: string) => {
    await new Promise((resolve) => setTimeout(resolve, 100))
    await queryClient.invalidateQueries({ queryKey: ['pockets', secret] })
    await queryClient.refetchQueries({ queryKey: ['pockets', secret] })

    return fetchLocalPockets()
  }

  return { pockets, addPocketMutation, updatePocketMutation, deletePocketMutation, syncMutation, refetchPockets }
}
